import styles from './style.module.scss';
import Button, { TERTIARY_THEME } from 'components/control/button';
import NotificationIcon from 'components/icon/notification-icon';
import { searchFilterIds } from 'constants/test-constants';
import {
  AVAILABLE_STATUS,
  NOT_AVAILABLE_OTHER_STATUS,
  NOT_AVAILABLE_SOLD_STATUS,
} from 'contexts/preferences/listing-params';
import CloseIcon from 'components/icon/close-icon';
import { PartialDeep } from 'type-fest';
import { FilterItemData, ListingFilterBarType, PriceRangeFilter, BedroomsFilter, BathroomsFilter, PropertyTypeFilterItem, StatusFilter, RentalFilter, SqftFilter, MiscFilter, ParkingSpacesFilter, HasImageFilter, LockerFilters, MaintenanceFeeFilters, ListedSinceFilter, ListedToFilter } from './listing_filter_bar';
import { useCallback } from 'react';
import formatDate from 'utils/format-date';
import { buildClassName } from 'utils/build-class-name';
interface FilterItemProps {
  filter: FilterItemData;
  onValueChange: (filters: PartialDeep<ListingFilterBarType>, changedFilter?: string) => void;
  removeFilter: (filterType: FilterItemData['type']) => void;
}
  
interface FilterConfig {
  label: string;
  render: (filter: FilterItemData) => React.ReactNode;
  removeAction: (onValueChange: (filters: PartialDeep<ListingFilterBarType>) => void) => void;
}

const filterConfigs: {[K in FilterItemData['type']]: FilterConfig } = {
  priceRange: {
    label: 'Price Range',
    render: filter => {
      const { min, max } = filter as PriceRangeFilter;
      if (min === null && max === null) return 'Any Price';
      if (min === null) return `Up to $${max?.toLocaleString()}`;
      if (max === null) return `From $${min?.toLocaleString()}+`;
      return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
    },
    removeAction: onValueChange => onValueChange({ priceMin: null, priceMax: null }),
  },
  bedrooms: {
    label: 'Bedrooms',
    render: filter => {
      const { bedrooms } = filter as BedroomsFilter;
      return bedrooms === '0+' ? 'Any Beds' : `${bedrooms} Beds`;
    },
    removeAction: onValueChange => onValueChange({ bedrooms: '0+' }),
  },
  bathrooms: {
    label: 'Bathrooms',
    render: filter => {
      const { bathrooms } = filter as BathroomsFilter;
      return bathrooms === '0+' ? 'Any Baths' : `${bathrooms} Baths`;
    },
    removeAction: onValueChange => onValueChange({ bathrooms: '0+' }),
  },
  propertyType: {
    label: 'Home Type',
    render: filter => {
      const { homeType } = filter as PropertyTypeFilterItem;
      const selectedTypes = Object.keys(homeType).filter(key => homeType[key]);
      if (selectedTypes.length === 0 || selectedTypes.length === 6) return 'Any Home Type';
      if (selectedTypes.length === 1) {
        const type = selectedTypes[0];
        // Capitalize We have a helper for this in utils, gotta test that it works the same
        return type.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
      }
      return `${selectedTypes.length} Home Types`;
    },
    removeAction: onValueChange => onValueChange({
      homeType: {
        commercial: true,
        condo: true,
        farm: true,
        house: true,
        land: true,
        townhouse: true,
      },
    }),
  },
  status: {
    label: 'Listing Status',
    render: filter => {
      const { status } = filter as StatusFilter;
      let parsedStatus: string;

      switch (status) {
        case NOT_AVAILABLE_SOLD_STATUS:
          parsedStatus = 'Sold';
          break;
        case NOT_AVAILABLE_OTHER_STATUS:
          parsedStatus = 'Expired';
          break;
        case AVAILABLE_STATUS:
          parsedStatus = 'For Sale';
          break;
        default:
          parsedStatus = 'For Sale';
          break;
      }

      return parsedStatus;
    },
    removeAction: onValueChange => onValueChange({ status: AVAILABLE_STATUS }),
  },
  rental: {
    label: 'Listing Type',
    render: filter => {
      const { rental } = filter as RentalFilter;
      return rental ? 'For Rent' : 'For Sale';
    },
    removeAction: onValueChange => onValueChange({ rental: false }),
  },
  sqft: {
    label: 'Sqft',
    render: filter => {
      const { sqftMin, sqftMax } = filter as SqftFilter;
      if (sqftMin === null && sqftMax === null) return 'Any Sqft';
      if (sqftMin === null) return `Up to ${sqftMax} Sqft`;
      if (sqftMax === null) return `From ${sqftMin} Sqft+`;
      return `${sqftMin} - ${sqftMax} Sqft`;
    },
    removeAction: onValueChange => onValueChange({ sqftMin: null, sqftMax: null }),
  },
  parkingSpaces: {
    label: 'Parking',
    render: filter => {
      const { parkingSpaces } = filter as ParkingSpacesFilter;
      return parkingSpaces && `${parkingSpaces} Parking`;
    },
    removeAction: onValueChange => onValueChange({ parkingSpaces: '0+' }),
  },
  hasImage: {
    label: 'Has Image',
    render: filter => {
      const { hasImage } = filter as HasImageFilter;
      return hasImage ? 'Has Image' : 'Show Listings With No Images';
    },
    removeAction: onValueChange => onValueChange({ hasImage: true }),
  },
  listedSince: {
    label: 'Listed Since',
    render: filter => {
      const { listedSince } = filter as ListedSinceFilter;
      if (!listedSince) return null;
      return `Listed Since ${formatDate(new Date(listedSince))}`;
    },
    removeAction: onValueChange => onValueChange({ listedSince: null }),
  },
  listedTo: {
    label: 'Listed To',
    render: filter => {
      const { listedTo } = filter as ListedToFilter;
      if (!listedTo) return null;
      return `Listed To ${formatDate(new Date(listedTo))}`;
    },
    removeAction: onValueChange => onValueChange({ listedTo: null }),
  },
  misc: {
    label: 'Misc',
    render: filter => {
      const { garage, openHouse, pool, waterfront, fireplace } = filter as MiscFilter;
      const filters = [];
      if (garage) filters.push('Garage');
      if (openHouse) filters.push('Open House');
      if (pool) filters.push('Pool');
      if (waterfront) filters.push('Waterfront');
      if (fireplace) filters.push('Fireplace');
      const formattedFilters = filters.slice(0, 2).join(', ');
      // Only show the first 2 filters
      return filters.length > 2 ? `${formattedFilters} + ${filters.length - 2}` : formattedFilters;
    },
    removeAction: onValueChange => onValueChange({
      garage: false,
      openHouse: false,
      pool: false,
      waterfront: false,
      fireplace: false,
    }),
  },
  locker: {
    label: 'Locker',
    render: filter => {
      const { locker } = filter as LockerFilters;
      if (locker) {
        return locker === 'yes' ? 'Has Locker' : 'No Locker';
      }
      return 'Locker Filter';
    },
    removeAction: onValueChange => onValueChange({
      additional: {
        condoOrTownhouse: {
          locker: 'any',
        },
      },
    }),
  },
  maintenanceFee: {
    label: 'Maintenance Fee',
    render: filter => {
      const { maintenanceFee } = filter as MaintenanceFeeFilters;
      return `Max $${maintenanceFee?.toLocaleString()} Maintenance Fee`;
    },
    removeAction: onValueChange => onValueChange({
      additional: {
        condoOrTownhouse: {
          maintenanceFee: null,
        },
      },
    }),
  },
};

const FilterItem: React.FC<FilterItemProps> = ({ filter, onValueChange, removeFilter }) => {
  const config = filterConfigs[filter.type];

  if (!config) {
    return null;
  }

  return (
    <div className={styles['active-filter-item']}>
      <span className={styles['filter-value']}>{config.render(filter)}</span>
      <CloseIcon
        onClick={() => {
          config.removeAction(onValueChange);
          removeFilter(filter.type);
        }}
        className={styles['remove-filter-button']}
      />
    </div>
  );
};

interface FilterChipBarProps {
  removeFilterItem?: (filterType: FilterItemData['type']) => void;
  onValueChange: (filters: PartialDeep<ListingFilterBarType>, changedFilter?: string) => void;
  onSaveSearchButtonClick?: () => void;
  activeFilterItems: FilterItemData[];
  setActiveFilterItems: (activeFilterItems: FilterItemData[]) => void;
  filters: ListingFilterBarType;
  isMobile?: boolean;
  oneLine?: boolean;
}

export const FilterChipBar = ({ onValueChange, onSaveSearchButtonClick, activeFilterItems, setActiveFilterItems, isMobile, oneLine = true }: FilterChipBarProps) => {


  const removeFilterItem = useCallback((filterType: FilterItemData['type']) => {
    const newFilterItems = activeFilterItems.filter(item => item.type !== filterType);
    setActiveFilterItems(newFilterItems);
  }, [activeFilterItems, setActiveFilterItems]);
  
  return (
    <div className={buildClassName(styles['active-filters-display'], !oneLine && styles['multi-line'])}>
      <>
        <div className={styles['active-filter-items-wrapper']}>
          {activeFilterItems.map((filter, index) => (
            <FilterItem
              key={`${filter.type}-${index}`}
              filter={filter}
              onValueChange={onValueChange}
              removeFilter={removeFilterItem}
            />
          ))}
        </div>
        {!isMobile && onSaveSearchButtonClick &&
          <Button
            theme={TERTIARY_THEME}
            label="Save Search"
            Icon={NotificationIcon}
            testId={searchFilterIds.saveSearchButtonMapPage}
            onClick={onSaveSearchButtonClick}
          />
        }
      </>
    </div>
  );
};